import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';

import { IconButton } from '@flipster/react/design-system/icon-button';
import ChevronLeftIcon from '@flipster/react/icon/generated/ChevronLeftIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import EllipsisHorizontalIcon from '@flipster/react/icon/generated/EllipsisHorizontalIcon';
import { center } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { competitionRankingsBunja } from '../state';

const PageButton = ({
  page,
  currentPage,
  onClick,
}: {
  page: number;
  currentPage: number;
  onClick: (page: number) => void;
}) => {
  const isActive = currentPage === page;
  return (
    <button
      key={`page-${page}`}
      type='button'
      onClick={() => onClick(page)}
      className={center({
        w: 30,
        h: 30,
        direction: 'column',
        gap: 10,
        rounded: 4,
        bg: isActive ? 'background.accent.secondary' : 'none',
        color: isActive ? 'inverse.black' : 'content.default.level-3',
        textStyle: 'Text/M/Monospace/Strong',
        textAlign: 'center',
        _hover: {
          bg: isActive ? 'background.accent.secondary' : 'background.interaction.hover',
        },
      })}
    >
      {page}
    </button>
  );
};

const Ellipsis = () => <EllipsisHorizontalIcon size={16} color={token('colors.content.default.level-3')} />;

type VisiblePage = number | 'ellipsis-start' | 'ellipsis-end';

/**
 * Get visible pages for pagination with fixed first and last page
 * - Always shows 1 and last page
 * - Shows current page with neighbors (current-1, current, current+1)
 * - Examples (totalPages=10):
 *   - Page 1: 1, 2, 3 ... 10
 *   - Page 2: 1, 2, 3 ... 10
 *   - Page 3: 1, 2, 3, 4 ... 10
 *   - Page 4: 1 ... 3, 4, 5 ... 10
 *   - Page 8: 1 ... 7, 8, 9, 10
 *   - Page 9: 1 ... 8, 9, 10
 */
const getVisiblePages = (currentPage: number, totalPages: number): VisiblePage[] => {
  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: VisiblePage[] = [];

  const showStartEllipsis = currentPage > 3;
  const showEndEllipsis = currentPage < totalPages - 2;

  pages.push(1);

  if (showStartEllipsis) {
    pages.push('ellipsis-start');
  }

  const start = showStartEllipsis ? Math.min(currentPage - 1, totalPages - 2) : 2;
  const end = showEndEllipsis ? Math.max(currentPage + 1, 3) : totalPages - 1;

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (showEndEllipsis) {
    pages.push('ellipsis-end');
  }

  // Always show last page
  pages.push(totalPages);

  return pages;
};

export const Pagination = () => {
  const { rankingsPaginationAtom, onOffsetChangeAtom } = useBunja(competitionRankingsBunja);
  const onOffsetChange = useSetAtom(onOffsetChangeAtom);
  const { total, offset, limit } = useAtomValue(rankingsPaginationAtom);

  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, limit)));
  const currentPage = Math.floor((offset || 0) / Math.max(1, limit)) + 1;

  const movePage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    const nextOffset = (page - 1) * Math.max(1, limit);
    onOffsetChange(nextOffset);
  };

  if (totalPages <= 1) return null;

  const visiblePages = getVisiblePages(currentPage, totalPages);

  return (
    <div className={center({ gap: 8, pt: 32 })}>
      <IconButton
        hierarchy='quaternary'
        size='medium'
        disabled={currentPage === 1}
        onClick={() => movePage(currentPage - 1)}
        icon={<ChevronLeftIcon />}
      />
      {visiblePages.map((item, index) => {
        if (item === 'ellipsis-start' || item === 'ellipsis-end') {
          return <Ellipsis key={item} />;
        }
        return <PageButton key={`page-${item}-${index}`} page={item} currentPage={currentPage} onClick={movePage} />;
      })}
      <IconButton
        hierarchy='quaternary'
        size='medium'
        disabled={currentPage === totalPages}
        onClick={() => movePage(currentPage + 1)}
        icon={<ChevronRightIcon />}
      />
    </div>
  );
};
